import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

const WALLET_UPDATED_EVENT = "heirwise-wallet-updated";

export function notifyWalletUpdated() {
  window.dispatchEvent(new CustomEvent(WALLET_UPDATED_EVENT));
}

export function useWallet() {
  const { user } = useAuth();
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchBalance = useCallback(async () => {
    if (!user) { setBalance(0); setLoading(false); return; }

    const { data: account } = await supabase
      .from("accounts")
      .select("balance")
      .eq("user_id", user.id)
      .eq("account_type", "checking")
      .maybeSingle();

    if (account) {
      setBalance((account).balance);
    } else {
      const { data } = await supabase
        .from("wallet_transactions")
        .select("amount")
        .eq("user_id", user.id);
      const total = (data || []).reduce((sum, t) => sum + t.amount, 0);
      setBalance(total);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  useEffect(() => {
    const handleWalletUpdated = () => {
      void fetchBalance();
    };

    window.addEventListener(WALLET_UPDATED_EVENT, handleWalletUpdated);
    return () => window.removeEventListener(WALLET_UPDATED_EVENT, handleWalletUpdated);
  }, [fetchBalance]);

  const addFunds = async (
    amount: number,
    reason: string,
    lessonId?: string,
    transactionType: string = "quiz_reward"
  ) => {
    if (!user) return;

    const { error } = await supabase.rpc("deposit_quiz_reward", {
      p_amount: amount,
      p_description: reason,
      p_transaction_type: transactionType,
      p_lesson_id: lessonId || null,
    });

    if (error) {
      // Server-side validation failed — do NOT fall back to a client-controlled
      // insert (would let users mint arbitrary balances from the console).
      console.warn("[useWallet] deposit_quiz_reward failed:", error.message);
      return;
    }

    setBalance(prev => prev + amount);
    notifyWalletUpdated();
  };

  return { balance, loading, addFunds, refetch: fetchBalance };
}
